// Pallet beacons (jobq-api pallet_beacons.py, 2026-10-06).
//
// A beacon is a battery BLE tag with an e-paper screen and a fixed ID (BCN-003). JobQ puts it
// on one material lot (TRK); a shop Pi pushes the lot to its screen and flashes its LED on
// "Find". Plugging it into the laser Pi's USB checks the pallet in at the laser, and the Add
// Sheet popup fills in its TRK.
//
// Everything here is advisory UI: a failed beacon call never blocks the page it sits on.

import config from '../config.js';
import { detailToString } from './apiError';
import { shopClock } from './shopDate';

const API_BASE = config.API_BASE_URL;

/** fetch JSON; resolves {ok, status, body, message} and never throws. */
export async function beaconFetch(path, opts = {}) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    let body = null;
    try { body = await res.json(); } catch (_) { body = null; }
    const detail = body && typeof body === 'object' ? body.detail : null;
    const message = res.ok ? null
      : (detail && typeof detail === 'object' && detail.message) || detailToString(detail) || `HTTP ${res.status}`;
    return { ok: res.ok, status: res.status, body, message };
  } catch (e) {
    return { ok: false, status: 0, body: null, message: 'Could not reach JobQ.' };
  }
}

/** Who is acting, for the beacon's history (an admin session's name; else nobody). */
export function currentActor() {
  try {
    const n = sessionStorage.getItem('userName');
    return n && n.trim() ? n.trim() : null;
  } catch (_) {
    return null;
  }
}

export function lotBeacon(mtnId) {
  return beaconFetch(`/material_tracking/${mtnId}/beacon`);
}

export function assignBeacon(mtnId, beaconId, { move = false, actor = null } = {}) {
  return beaconFetch(`/material_tracking/${mtnId}/beacon`, {
    method: 'PUT', body: JSON.stringify({ beacon_id: beaconId, move, actor }),
  });
}

/**
 * Put a beacon on a lot, asking first when it is on another lot that still has steel: the API
 * answers 409 {error: 'beacon_in_use', message}, `confirm(message)` asks, and a yes sends it again
 * with move:true. The ONE copy of that flow (BeaconCard's dropdown, the Beacons page's assign box).
 * Resolves the last answer ({ok, status, body, message}, plus moved:true after a move) or
 * {ok:false, cancelled:true} when the person said no. Never throws.
 */
export async function assignBeaconAsking(mtnId, beaconId, { actor = null, confirm } = {}) {
  // eslint-disable-next-line no-alert
  const ask = confirm || ((m) => window.confirm(m));
  const r = await assignBeacon(mtnId, beaconId, { actor });
  const inUse = !r.ok && r.status === 409 && r.body && r.body.detail && r.body.detail.error === 'beacon_in_use';
  if (!inUse) return r;
  // `confirm` may answer later (a phone's panel resolves a promise).
  if (!(await ask(r.message))) return { ok: false, cancelled: true, status: r.status, body: r.body, message: null };
  const moved = await assignBeacon(mtnId, beaconId, { actor, move: true });
  return moved.ok ? { ...moved, moved: true } : moved;
}

export function releaseBeacon(mtnId, actor = null) {
  const q = actor ? `?actor=${encodeURIComponent(actor)}` : '';
  return beaconFetch(`/material_tracking/${mtnId}/beacon${q}`, { method: 'DELETE' });
}

export function findLot(mtnId, opts = {}) {
  return beaconFetch(`/material_tracking/${mtnId}/find`, { method: 'POST', body: JSON.stringify(opts) });
}

export function findBeacon(beaconId, opts = {}) {
  return beaconFetch(`/beacons/${encodeURIComponent(beaconId)}/find`, { method: 'POST', body: JSON.stringify(opts) });
}

// ── The Beacons list (pages/BeaconsPage.jsx and the phone's mobile/screens/BeaconsScreen.jsx) ──

/**
 * What a beacon is to the Beacons page, from GET /beacons (null = not registered):
 * 'unregistered' | 'retired' | 'on_pallet' (on a lot that still has steel) | 'on_so_pallet' (on a
 * sales-order pallet, `b.pallet`; its `lot` is null, 2026-10-22) | 'on_crate' (on an open surplus crate,
 * `b.crate`, 2026-11-03) | 'free' (incl. one on a finished lot or an emptied crate: the API counts it free).
 */
export function beaconState(b) {
  if (!b) return 'unregistered';
  if (b.state === 'retired') return 'retired';
  if (b.effective_state === 'assigned' && b.lot) return 'on_pallet';
  if (b.pallet) return 'on_so_pallet';
  if (b.effective_state === 'assigned' && b.crate && b.crate.status === 'open') return 'on_crate';
  return 'free';
}

/** A surplus crate as one line: "Surplus crate · FAB31162 · 8 pcs" / "Owed crate · FAB31162 → SO-1150 · 5 pcs". */
export function crateText(c) {
  if (!c) return '';
  const kind = c.kind === 'owed' ? `Owed crate${c.owed_order_number ? ` (${c.owed_order_number})` : ''}` : 'Surplus crate';
  const pcs = Number(c.in_crate || 0) + Number(c.coming || 0);
  return [kind, c.product_id || (c.part_id ? `part ${c.part_id}` : ''), `${pcs} pcs`].filter(Boolean).join(' · ');
}

/** A sales-order pallet as one line: "PLT-Z9HJ · SO-1144 · Faith Technologies" (a beacon view's
 *  `pallet`, or a task pallet entry). Empty parts are left out. */
export function soPalletText(p) {
  if (!p) return '';
  return [p.short_id || p.label || (p.pallet_id ? `Pallet ${p.pallet_id}` : ''), p.so_number, p.customer]
    .filter((x) => x && String(x).trim()).join(' · ');
}

/**
 * Put a beacon on the pallet whose TRK number a person typed or scanned (the ?beacon= assign box).
 * Looks at the lot first: a finished lot needs no beacon, and one that already has a beacon would
 * silently give it up (the PUT replaces it), so `ui.confirm` asks; a beacon on another live lot asks
 * too (assignBeaconAsking). Resolves {tone, text, assigned} to show, or {cancelled: true} (a no,
 * nothing to show). Never throws.
 */
export async function assignBeaconByTrk({ beaconId, lotText, ui, actor = null }) {
  const mtnId = parseLotNumber(lotText);
  if (!mtnId) return { tone: 'danger', text: "Type the TRK number from the pallet's label, e.g. 000409." };
  const trk = trkText(mtnId);
  const lot = await lotBeacon(mtnId);
  if (!lot.ok) {
    return { tone: 'danger', text: lot.status === 404 ? `TRK ${trk} isn't in JobQ. Check the number on the label.` : lot.message };
  }
  if (lot.body.lot_done) {
    return {
      tone: 'danger',
      text: `TRK ${trk} is ${lot.body.lot_status}: nothing left on that pallet to track. Pick a pallet that still has steel.`,
    };
  }
  const current = lot.body.beacon;
  if (current && current.beacon_id === beaconId) return { tone: 'success', text: `${beaconId} is already on TRK ${trk}.` };
  if (current) {
    const ok = await ui.confirm(
      `TRK ${trk} already has ${current.beacon_id}. Put ${beaconId} on it instead? ${current.beacon_id} goes back to the pool.`,
      { title: 'Replace the beacon?', confirmLabel: 'Put it on' },
    );
    if (!ok) return { cancelled: true };
  }
  const r = await assignBeaconAsking(mtnId, beaconId, {
    actor, confirm: (m) => ui.confirm(m, { title: 'Move the beacon?', confirmLabel: 'Move it' }),
  });
  if (r.cancelled) return { cancelled: true };
  if (!r.ok) return { tone: 'danger', text: r.message };
  return { tone: 'success', text: `${beaconId} is on TRK ${trk}. Its screen shows the pallet within a few seconds.`, assigned: true };
}

/** How often the Beacons list re-reads GET /beacons. */
export const BEACON_LIST_REFRESH_MS = 15000;

/** GET /beacons: {ok, status, body: {beacons, server_time}, message}. Never throws. */
export function loadBeacons() {
  return beaconFetch('/beacons');
}

/**
 * Where a beacon is: "Plugged in at Laser", "In range (shipping-pi)", "Last heard 2 d ago", and,
 * when the API sends a location (jobq-api LocationView, the shop map, 2026-10-20), its place:
 * "In range (shipping-pi) · Metal room · near Rack A", "Last heard 2 d ago · Metal room · near
 * Rack A (Oct 18, 10:02 AM)", "… · roughly Main shop (…)" for a gateway-only guess.
 */
export function beaconWhere(b, now = Date.now()) {
  if (b.docked_station) return `Plugged in at ${b.docked_station}`;
  const base = b.in_range ? `In range (${b.last_seen_gateway || 'gateway'})` : `Last heard ${lastSeenLabel(b.last_seen_at, now)}`;
  const loc = b.location;
  const place = locationPlace(loc);
  if (!place || !loc || (loc.best !== 'scan' && loc.best !== 'gateways')) return base;
  const clock = locationFresh(loc, now) ? '' : shopClock(loc.scanned_at, new Date(now));
  return `${base} · ${loc.rough || loc.best === 'gateways' ? 'roughly ' : ''}${place}${clock ? ` (${clock})` : ''}`;
}

// ── Location on the shop map (jobq-api beacon_location.py LocationView, 2026-10-20) ──

/** A location reading this recent reads "now". */
export const LOC_NOW_S = 120;

/** "Metal room · near Rack A" from a LocationView (area + near spot, else the API's label); '' if none. */
export function locationPlace(loc) {
  if (!loc) return '';
  // a map with pallet zones answers in zones (jobq-api beacon_location.zone_answer): the best one,
  // or the best two while the best is under 70 % ("Press brake area or Main metal rack")
  const zone = loc.zone && loc.zone.name ? String(loc.zone.name).trim() : '';
  if (zone) {
    const zone2 = loc.zone2 && loc.zone2.name ? String(loc.zone2.name).trim() : '';
    return zone2 ? `${zone} or ${zone2}` : zone;
  }
  const area = loc.area ? String(loc.area).trim() : '';
  const near = loc.near ? String(loc.near).trim() : '';
  if (area || near) return [area, near ? `near ${near}` : ''].filter(Boolean).join(' · ');
  return loc.label ? String(loc.label).trim() : '';
}

/** Seconds since the location's scan (from scanned_at, else the API's age_s); null if unknown. */
export function locationAgeS(loc, now = Date.now()) {
  if (!loc) return null;
  const t = loc.scanned_at ? new Date(loc.scanned_at).getTime() : NaN;
  if (Number.isFinite(t)) return Math.max(0, Math.round((Number(now) - t) / 1000));
  return Number.isFinite(loc.age_s) ? loc.age_s : null;
}

/** Whether the location was read within LOC_NOW_S (or right after the Find that asked for it). */
export function locationFresh(loc, now = Date.now()) {
  if (!loc) return false;
  if (loc.after_find) return true;
  const age = locationAgeS(loc, now);
  return age !== null && age <= LOC_NOW_S;
}

/** The list's headline counts. "Low" = a reported battery at 20 % or less. */
export function beaconCounts(list) {
  const rows = Array.isArray(list) ? list : [];
  return {
    total: rows.length,
    onPallets: rows.filter((b) => b.effective_state === 'assigned').length,
    free: rows.filter((b) => b.effective_state === 'available').length,
    low: rows.filter((b) => Number.isFinite(b.battery_pct) && b.battery_pct <= 20).length,
  };
}

/**
 * Find (flash the LED): the request and the sentence to show, {tone, text, find}. `find` is the
 * API's answer when it can be followed (command_id + progress: render <FindProgress find={find}>
 * instead of the sentence); an older API answers without them. Never throws.
 */
export async function findBeaconSaying(beaconId, actor = null) {
  const r = await findBeacon(beaconId, { actor });
  if (!r.ok) return { tone: 'danger', text: r.message };
  const find = r.body && r.body.command_id && r.body.progress ? r.body : null;
  return {
    tone: 'success',
    text: `${beaconId}: ${r.body && r.body.in_range ? 'flashing within a few seconds' : 'flashes when a gateway reaches it'}.`,
    find,
  };
}

// ── Find progress (jobq-api GET /beacon_finds/{id}, pallet_beacons.find_progress, 2026-10-14) ──
// A Find goes to every gateway that hears the beacon; the first to light it wins. The UI follows
// it (queued -> sent to <gateway> -> lit / failed) so nobody clicks Find again; a second click
// gets the same find back (it no longer cancels the try in flight).

/** How often a landing Find is re-read, and for how long at most (a find lasts 5 min). */
export const FIND_POLL_MS = 1500;
export const FIND_POLL_FOR_MS = 6 * 60 * 1000;
/**
 * After a Find lands, a beacon on firmware 0.8+ scans the anchors and the gateway reports where it
 * is a little later (LocationView.awaiting_scan): keep re-reading every LOC_FOLLOW_MS, for at most
 * LOC_WAIT_MS after the find finished, so the map moves to "now".
 */
export const LOC_FOLLOW_MS = 3000;
export const LOC_WAIT_MS = 60 * 1000;

/** GET /beacon_finds/{id}: {ok, status, body: progress, message}. Never throws. */
export function loadFind(commandId) {
  return beaconFetch(`/beacon_finds/${encodeURIComponent(commandId)}`);
}

const shortReason = (s) => {
  const t = String(s || '').replace(/^ble:\s*/i, '').trim();
  return t.length > 60 ? `${t.slice(0, 59)}…` : t;
};
const secs = (p) => (Number.isFinite(p && p.elapsed_s) ? ` (${Math.round(p.elapsed_s)} s)` : '');
const gatewayList = (ids) => (ids.length <= 2 ? ids.join(' and ') : `${ids.slice(0, -1).join(', ')} and ${ids[ids.length - 1]}`);

/**
 * The words for a Find in progress: {tone, text, busy (still landing), terminal, retry (offer Try
 * again)}. `p` is a GET /beacon_finds answer (or the `progress` of a POST find), null before the
 * first answer.
 */
export function findProgressText(p, beaconId = null) {
  const id = (p && p.beacon_id) || beaconId || 'The beacon';
  if (!p) return { tone: 'primary', text: `Sending the Find for ${id}…`, busy: true, terminal: false, retry: false };
  const gws = Array.isArray(p.gateways) ? p.gateways : [];
  switch (p.phase) {
    case 'sent':
      return { tone: 'primary', text: `Sent to ${gatewayList(gws)}: connecting to ${id}…${secs(p)}`, busy: true,
        terminal: false, retry: false };
    case 'lit': {
      const mins = Number(p.duration_s) > 0 ? ` for about ${Math.max(1, Math.round(Number(p.duration_s) / 60))} min` : '';
      const took = Number.isFinite(p.elapsed_s) ? ` in ${Math.round(p.elapsed_s)} s` : '';
      return { tone: 'success', text: `${id} is flashing${mins}: lit by ${p.lit_by || 'a gateway'}${took}.`,
        busy: false, terminal: true, retry: false };
    }
    case 'failed':
      return { tone: 'danger',
        text: `Couldn't reach ${id}${p.last_failure || p.result ? ` (${shortReason(p.last_failure || p.result)})` : ''}. `
          + 'Bring it closer to a gateway or check its battery, then try again.',
        busy: false, terminal: true, retry: true };
    case 'expired':
      return { tone: 'warning', text: `No gateway heard ${id} in 5 minutes. Is it charged and out on the floor?`,
        busy: false, terminal: true, retry: true };
    case 'cancelled':
      return { tone: 'secondary', text: 'Replaced by a newer Find.', busy: false, terminal: true, retry: false };
    case 'gone': // JobQ doesn't know this find (an older API): nothing to show
      return { tone: 'secondary', text: '', busy: false, terminal: true, retry: false };
    case 'stale': // this page stopped following it (FIND_POLL_FOR_MS) before it finished
      return { tone: 'secondary',
        text: `Stopped following the Find for ${id} after ${Math.round(FIND_POLL_FOR_MS / 60000)} minutes. `
          + 'The Beacons page shows whether it lit.',
        busy: false, terminal: true, retry: true };
    default: // queued
      if (p.tries > 0) {
        return { tone: 'primary',
          text: `Trying ${id} again${p.last_failure ? ` (last try: ${shortReason(p.last_failure)})` : ''}…${secs(p)}`,
          busy: true, terminal: false, retry: false };
      }
      return { tone: 'primary', text: `Waiting for a gateway to hear ${id}…${secs(p)}`, busy: true, terminal: false,
        retry: false };
  }
}

/**
 * A find to follow from a beacon view's pending_find (after a reload, or one started elsewhere).
 * Only from an API that can answer GET /beacon_finds (its pending_find carries `phase`): an older
 * API's pending_find has a command_id too, and following it would poll a 404 for minutes.
 */
export function findFromPending(b) {
  const pf = b && b.pending_find;
  if (!pf || !pf.command_id || !pf.phase) return null;
  return { command_id: pf.command_id, beacon_id: b.beacon_id, progress: null };
}

export function dockedBeacons(station) {
  return beaconFetch(`/beacons/docked?station=${encodeURIComponent(station)}`);
}

/** 'BCN-003' from '3', 'bcn3', 'BCN-003'; null when it can't be one (mirrors the API). */
export function normalizeBeaconId(raw) {
  if (raw === null || raw === undefined) return null;
  const m = String(raw).trim().toUpperCase().match(/^(?:BCN-?)?0*([0-9]{1,3})$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (n < 1 || n > 999) return null;
  return `BCN-${String(n).padStart(3, '0')}`;
}

/** The number printed on a material label: mtn_id zero-padded to 6 ('000409'). */
export function trkText(mtnId) {
  return String(mtnId).padStart(6, '0');
}

/**
 * The lot (mtn_id) a person typed or scanned: '409', '000409', 'TRK 000409', 'trk-409',
 * 'MTN 409', or a lot link (…/s/mtn-409). Null when it can't be one. The TRK number IS the
 * mtn_id, zero-padded.
 */
export function parseLotNumber(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  const m = s.match(/\/s\/mtn-?(\d{1,9})\/?$/i) || s.match(/^(?:TRK|MTN)?[-\s#]*(\d{1,9})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return n > 0 ? n : null;
}

/** The link the beacon's screen QR holds (GET /beacons screen.qr / content.qr), or null. */
export function screenQr(b) {
  if (!b || !b.screen) return null;
  return b.screen.qr || (b.screen.content && b.screen.content.qr) || null;
}

export function batteryLabel(b) {
  if (!b) return '—';
  if (Number.isFinite(b.battery_pct)) return `${b.battery_pct}%`;
  if (Number.isFinite(b.battery_mv)) return `${(b.battery_mv / 1000).toFixed(2)} V`;
  return '—';
}

/**
 * Badge for a beacon that needs a top-off, from the API's `battery_state`
 * (jobq-api pallet_beacons.battery_state owns the thresholds): low ≈ 20 %,
 * critical ≈ 10 %. Null when it doesn't need one (or is charging).
 */
export function chargeBadge(b) {
  if (!b) return null;
  if (b.battery_state === 'critical') return { text: 'Charge now', className: 'bg-danger' };
  if (b.battery_state === 'low') return { text: 'Needs charging', className: 'bg-warning text-dark' };
  return null;
}

/**
 * The firmware line under a beacon (jobq-api "Pallet beacon firmware updates at the dock"):
 * {text, tone} or null when there is nothing to say. `firmware_target` is set only while the
 * beacon is due an update; `firmware_status` is the dock gateway's latest word on it
 * ({state: waiting | updating | blocked, reason}). An API without these fields gives just the version.
 */
export function firmwareNote(b) {
  if (!b) return null;
  const fw = b.firmware_version || null;
  const st = b.firmware_status && typeof b.firmware_status === 'object' ? b.firmware_status : null;
  const target = b.firmware_target || null;
  if (b.firmware_hold) {
    const why = b.firmware_hold_reason ? `: ${b.firmware_hold_reason}` : '';
    return { text: `fw ${fw || '?'} · updates held${why}`, tone: 'warning' };
  }
  if (target && st && st.state === 'updating') {
    return { text: `Updating firmware ${fw || '?'} → ${target}…`, tone: 'info' };
  }
  if (target) {
    const why = st && st.reason && (st.state === 'waiting' || st.state === 'blocked') ? ` (${st.reason})` : '';
    return { text: `fw ${fw || '?'} → ${target}: dock it at the laser to update${why}`, tone: st && st.state === 'blocked' ? 'danger' : 'primary' };
  }
  return fw ? { text: `fw ${fw}`, tone: 'muted' } : null;
}

/** "just now", "4 min ago", "3 h ago", "2 d ago", "never". */
export function lastSeenLabel(iso, now = Date.now()) {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 'never';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/** One short line for a dropdown option or a chip. */
export function beaconOptionLabel(b) {
  const bits = [b.beacon_id, batteryLabel(b)];
  bits.push(b.in_range ? 'in range' : `seen ${lastSeenLabel(b.last_seen_at)}`);
  return bits.join(' · ');
}

/** What the screen is doing, for the card: {text, tone}. */
export function screenStatus(b) {
  if (!b || !b.screen) return { text: '', tone: 'muted' };
  if (b.screen.current) return { text: 'On screen', tone: 'success' };
  if (b.in_range || b.docked_station) return { text: 'Updating the screen…', tone: 'info' };
  return { text: 'Screen updates when a gateway next hears it', tone: 'warning' };
}

/**
 * Which lot the laser's Add Sheet should fill in from the docked beacons.
 * Exactly one docked pallet with a lot -> its TRK. More than one -> null (the popup shows
 * chips to pick). The Add Sheet's own material check still catches a wrong pallet.
 */
export function dockedLots(docked) {
  return (Array.isArray(docked) ? docked : [])
    .filter((b) => b && b.lot && !b.lot_done && b.in_range !== false && b.lot.lot_status !== 'deleted');
}

export function dockedPrefill(docked) {
  const lots = dockedLots(docked);
  return lots.length === 1 ? String(lots[0].lot.mtn_id) : null;
}

/** Laser Find Material: the ONE pallet to use next, flashed when it has a beacon (jobq-api
 *  POST /laser/{task}/find_material; the rules live in pallet_beacons.pick_next_pallet). */
export function findMaterial(taskId, opts = {}) {
  return beaconFetch(`/laser/${encodeURIComponent(taskId)}/find_material`, { method: 'POST', body: JSON.stringify(opts) });
}
